import { AsyncValidator, AsyncValidatorFn, FormControl, FormGroup, Validator, ValidatorFn, Validators } from "@angular/forms";
import moment from "moment";
import { NtCompany } from "./nt-company";

export interface IGroupContainer {
    title: string;
    dialogMode: boolean;
    description?: string;
    groups: IGroup[];
    cancelAction?: Function;
    completeAction?: Function;
    debugAction?: Function;
    actionText?: string;
    hideCancelButton?: boolean;
    form?: FormGroup;
    collapseGroupNav?: boolean;
    data?: any;
    urlEnabled?: boolean;
    urlBase?: string;
    showCompanyStatus?: boolean;
    company?: NtCompany;
}

export interface IGroup {
    header: string;
    sections: ISection[];
    id: string;
    url?: string;
}

export interface ISection {
    id?: string;
    header: string;
    description?: string;
    allowEdit: boolean;
    items: IItem[];
    component?: any;
    componentProps?: ISectionComponentProp[];
    componentOutputs?: ISectionComponentOutput[];
    componentUpdate?: Function,
    data?: any;
    form?: FormGroup;
    completedAction?: ISectionCompletedFunction;
    isChild?: boolean;
}

export interface ISectionCompletedFunction {
    (section: ISection): Promise<boolean>;
}

export interface ISectionComponentProp {
    propName: string;
    data: any;
}

export interface ISectionComponentOutput {
    outputName: string;
    handler: Function;
}

export interface IItem {
    label: string;
    fieldName: string;
    displayFieldName?: string,
    control: string;
    validation?: string;
    dropdownList?: IKeyValue[];
    tooltip?: string;
    tooltipKey?: string;
    valueFormat?: string;
    controlDataType?: string;
    isFieldReadOnly?: boolean;
    isFieldRequired?: boolean;
    validatorsSync?: ValidatorFn[];
    validatorsAsync?: AsyncValidatorFn[];
    preventEdit?: boolean;
    updateMethod?: Function;
    validatorFn?: Function,
    typeAheadData?: any[]
    typeAheadHelperFunction?: Function;
    minDate?: Date;
    maxDate?: Date;
}

export interface IKeyValue {
    key: any;
    value: any;
}

///Ideally, add FormArray to the possible input types, but array indexing does not compile
export const getFormDirtyValues = ( formItem: FormGroup | FormControl,
    updatedValues: any,
    name?: string) => {
    if (formItem instanceof FormControl) {
        if (name && formItem.dirty) {    
            updatedValues[name] = formItem.value;
        }
    } else {
        for (const formControlName in formItem.controls) {
            if (formItem.controls.hasOwnProperty(formControlName)) {
                //Attempt to resolve array indexing, but for now limit to FormGroup and FormControl
                //const formControl = (formItem instanceof FormArray) ? (formItem as FormArray).controls.find(x=> x. formControlName) : (formItem as FormGroup).controls[formControlName];    //  formItem.controls[formControlName];
                const formControl = formItem.controls[formControlName];

                if (formControl instanceof FormControl) {
                    getFormDirtyValues(formControl, updatedValues, formControlName);
                // } else if (
                //         formControl instanceof FormArray &&
                //         formControl.dirty &&
                //         formControl.controls.length > 0 ) {
                //     updatedValues[formControlName] = [];
                //     getFormDirtyValues(formControl, updatedValues[formControlName]);
                }else if (formControl instanceof FormGroup && formControl.dirty) {
                    updatedValues[formControlName] = {};
                    getFormDirtyValues(formControl, updatedValues[formControlName]);
                }
            }
        }
    }
}

export const buildFormFromGroupConfiguration = (config: IGroupContainer, data: any, form: FormGroup) => {
    config.groups.forEach(g => {
        buildFormFromGroup(g, data, form);
    })
}

export const buildFormFromGroup = (group: IGroup, data: any, form: FormGroup) => {
    group.sections.forEach( s=> {
        buildFormFromSection(s, data, form);
    });
}

export const buildFormFromSection = (section: ISection, data: any, form: FormGroup) => {
    section.items.forEach(f => {
        if(data){
            const value = getValueFromPropertyName(data, f.fieldName);
            buildFormControlFromEditorField(f, value, form);
        }else{
            buildFormControlFromEditorField(f, null, form);
        }
    });
}

export const buildFormFromItemFieldList = (fields: IItem[], data: any, form: FormGroup) => {
    fields.forEach(f => {
        if(data){
            const value = getValueFromPropertyName(data, f.fieldName);
            buildFormControlFromEditorField(f, value, form);
        }else{
            buildFormControlFromEditorField(f, null, form);
        }
    });
}

export const getValueFromPropertyName = (data: any, fieldName: string) => {
    //Support using nested property names
    const fieldNameParts = fieldName.split('.');
    let value = data;
    fieldNameParts.forEach(name => {
        value = value[name];
    });

    return value;
}

export const buildFormControlFromEditorField = (field: IItem, value: any, form: FormGroup) => {
    let useValue: any = value;
    if(field.control == 'datepicker'){
        //console.log("Transforming value to moment);")
        // if(value){
        //     useValue = moment(value);
        // }else{
        //     useValue = moment();
        // }
    }else{
        useValue = value;
    }
    const control = new FormControl(useValue);

    if(field.validatorsSync){
        control.addValidators(field.validatorsSync);
    }
    if(field.validatorsAsync){
        control.addAsyncValidators(field.validatorsAsync);
    }

    if(field.isFieldRequired){
        control.addValidators(Validators.required);
    }

    if(field.isFieldReadOnly){
        control.disable();
    }
    
    form.addControl(field.fieldName, control);
}

export const formatDate = (date: any, isUSFormat?: boolean) => {
    const m = moment(date);
    if(isUSFormat){ 
        return m.format("MM/DD/yyyy")
    }else{
        return m.format("DD/MM/yyyy")
    }
}

export const formatDateTime = (date: any, isUSFormat?: boolean) => {
    const m = moment(date);
    if(isUSFormat){ 
        return m.format("MM/DD/yyyy HH:mm")
    }else{
        return m.format("DD/MM/yyyy HH:mm")
    }
}

export const formatDateTimeEx = (date: any, isUSFormat?: boolean) => {
    const m = moment(date);
    if(isUSFormat){ 
        return m.format("MM/DD/yyyy HH:mm:ss.SSS")
    }else{
        return m.format("DD/MM/yyyy HH:mm:ss.SSS")
    }
    
}

export const fixDecimalPlaces = (num: number, dps?: number): number => {
    if(!dps){
        dps = 2;
    }
    
    return parseFloat(num.toFixed(dps));
}

export const fixDecimalPlacesFormat = (num: number): string => {
    return num.toFixed(2);
}

export const formatCurrency = (num: any, fractionDigits?: number) => {
    const frac = fractionDigits === undefined ?  2: fractionDigits;
    const intl = new Intl.NumberFormat('en-GB',
        {
            style: 'decimal',
            minimumFractionDigits: frac,
            maximumFractionDigits: frac,
        });
    let result = intl.format(Math.abs(num));
    if(num < 0){
        result = `(${result})`;
    }
    return result;
}

export const formatPercentage = (num: any) => {
    return fixDecimalPlaces(num * 100, 1).toString() + '%';
}